import { get } from 'lodash-es';
import { getCanonical } from 'site-modules/shared/utils/seo-helper';
import { stripHtml } from 'client/utils/string-utils';
import { createDate } from 'client/site-modules/shared/utils/date-utils';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { getReviewedItem } from 'client/seo/schemas/review';
import { getOrganizationSchema } from './organization';

export function getAuthorSchema(authorName) {
  return {
    '@type': 'Person',
    name: authorName,
    url: `https://www.edmunds.com/about/authors/${makeNiceName(authorName)}.html`,
  };
}

/**
 *
 * @param {Object} articleObject
 * @param {String} image
 * @param {String} author
 * @param {String} articleType Article or NewsArticle or ReviewNewsArticle
 * @param {String} articleBody
 * @param {String[]} speakableSelectors
 * @param {String} keywords
 * @param {Object} contentVehicle
 * @return {Object}
 */
export function getArticleSchema({
  articleObject,
  image,
  author,
  articleType = 'Article',
  articleBody,
  speakableSelectors,
  keywords,
}) {
  const { title, subtitle, summary, updated, published, authors, vehicles } = articleObject;
  const canonical = getCanonical(articleObject);
  const authorName = author || get(authors, '[0].name', '');
  const publisher = getOrganizationSchema({ isTextLogo: articleType === 'NewsArticle' });
  const contentVehicle = vehicles?.[0];

  return {
    '@type': articleType,
    name: title,
    headline: title,
    articleBody: articleBody ? stripHtml(articleBody) : undefined,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonical,
    },
    description: summary,
    datePublished: published,
    dateModified: updated,
    alternativeHeadline: subtitle,
    publisher,
    image,
    author: authorName ? getAuthorSchema(authorName) : publisher,
    keywords: keywords || undefined,
    ...(speakableSelectors && {
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: speakableSelectors,
      },
    }),
    ...(articleType === 'ReviewNewsArticle' &&
      !!contentVehicle?.make &&
      getReviewedItem({
        makeModelSubmodelYear: {
          make: { name: contentVehicle.make },
          model: { name: contentVehicle.model },
          year: contentVehicle.year,
        },
      })),
  };
}

export function getEditorialNewsArticleSchema({ article, author }) {
  const { title, image, url, summary, publishDate, updateDate } = article;

  return {
    '@type': 'NewsArticle',
    name: title,
    headline: title,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.edmunds.com${url}`,
    },
    description: summary,
    datePublished: publishDate,
    dateModified: updateDate,
    publisher: getOrganizationSchema(),
    image,
    author: getAuthorSchema(author),
  };
}

export function getCarNewsArticleSchema(content) {
  const {
    authorName,
    'media-image': image,
    'article-date': date,
    'promo-text': promoText,
    'link-url': url,
    title,
  } = content.getAllMetadata();

  const articleDate = createDate(date);
  const hasPromoText = promoText && promoText !== '&nbsp;';
  const articleTitle = hasPromoText ? promoText : title;

  if (!articleTitle || !url) return null;

  return {
    '@type': 'NewsArticle',
    name: articleTitle,
    headline: articleTitle,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.edmunds.com${url}`,
    },
    datePublished: articleDate,
    dateModified: articleDate,
    publisher: getOrganizationSchema(),
    image,
    author: getAuthorSchema(authorName),
  };
}
